import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PrintNodePrinter {
  id: number;
  name: string;
  description: string;
  capabilities: string[];
  default: boolean;
  state: 'online' | 'offline';
}

export interface PrintJob {
  printerId: number;
  content: string;
  title?: string;
  copies?: number;
  contentType?: 'pdf_base64' | 'pdf_uri' | 'raw_base64' | 'raw_uri';
}

export type PrintContentFormat = 'html' | 'pdf';

export const usePrintNode = () => {
  const [printers, setPrinters] = useState<PrintNodePrinter[]>([]);
  const [selectedPrinter, setSelectedPrinter] = useState<PrintNodePrinter | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Load selected printer from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('selectedPrinter');
    console.log('Loading saved printer from localStorage:', saved);
    if (saved) {
      try {
        const savedPrinter = JSON.parse(saved);
        console.log('Parsed saved printer:', savedPrinter);
        setSelectedPrinter(savedPrinter);
      } catch (e) {
        console.error('Failed to parse saved printer:', e);
        localStorage.removeItem('selectedPrinter');
      }
    }
  }, []);

  // Save selected printer to localStorage
  useEffect(() => {
    if (selectedPrinter) {
      console.log('Saving printer to localStorage:', selectedPrinter);
      localStorage.setItem('selectedPrinter', JSON.stringify(selectedPrinter));
    }
  }, [selectedPrinter]);

  // Validate selected printer against available printers
  useEffect(() => {
    if (selectedPrinter && printers.length > 0) {
      const isStillAvailable = printers.find(p => p.id === selectedPrinter.id);
      console.log('Validating selected printer:', selectedPrinter.id, 'Available:', !!isStillAvailable);
      
      if (!isStillAvailable) {
        console.log('Selected printer no longer available, clearing selection');
        setSelectedPrinter(null);
        localStorage.removeItem('selectedPrinter');
        toast({
          title: "Printer Unavailable",
          description: `${selectedPrinter.name} is no longer available. Please select another printer.`,
          variant: "destructive",
        });
      } else if (isStillAvailable.state !== selectedPrinter.state) {
        // Update printer state if it changed
        console.log('Updating printer state from', selectedPrinter.state, 'to', isStillAvailable.state);
        setSelectedPrinter(isStillAvailable);
      }
    }
  }, [printers, selectedPrinter, toast]);

  const fetchPrinters = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('printnode-print', {
        body: { action: 'printers' }
      });

      if (error) throw error;

      if (data.success) {
        // Filter online printers for normal use but show all in UI
        const onlinePrinters = data.printers.filter((p: PrintNodePrinter) => p.state === 'online');
        setPrinters(data.printers); // Store all printers for UI display
        
        // Auto-select first online printer only if none selected and no saved printer exists
        const savedPrinter = localStorage.getItem('selectedPrinter');
        console.log('🔍 Checking auto-select conditions:', {
          hasSelectedPrinter: !!selectedPrinter,
          hasSavedPrinter: !!savedPrinter,
          totalPrinters: data.printers.length,
          onlinePrinters: onlinePrinters.length
        });
        
        if (!selectedPrinter && !savedPrinter && onlinePrinters.length > 0) {
          console.log('✅ Auto-selecting first online printer:', onlinePrinters[0].name);
          setSelectedPrinter(onlinePrinters[0]);
        } else if (savedPrinter && !selectedPrinter) {
          console.log('🔄 Found saved printer preference, will be loaded by localStorage effect');
        }
        
        const statusMessage = onlinePrinters.length > 0 
          ? `Found ${onlinePrinters.length} online printer(s)`
          : `Found ${data.printers.length} printer(s) but none are online`;
        
        toast({
          title: "Printers Loaded",
          description: statusMessage,
          variant: onlinePrinters.length === 0 ? "destructive" : "default"
        });
      } else {
        throw new Error(data.error || 'Failed to fetch printers');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch printers';
      setError(errorMessage);
      toast({
        title: "Printer Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const submitPrintJob = async (job: PrintJob): Promise<boolean> => {
    if (!selectedPrinter) {
      toast({
        title: "No Printer Selected",
        description: "Please select a printer first",
        variant: "destructive",
      });
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('printnode-print', {
        body: {
          action: 'print',
          printerId: selectedPrinter.id,
          content: job.content,
          title: job.title,
          copies: job.copies,
          contentType: job.contentType,
        }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Print Successful",
          description: `Label sent to ${selectedPrinter.name}`,
        });
        return true;
      } else {
        throw new Error(data.error || 'Print job failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Print job failed';
      setError(errorMessage);
      toast({
        title: "Print Failed",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const generateCanvasPrintContent = async (
    template: any,
    product: any,
    format: PrintContentFormat = 'html',
    templateDimensions?: { width: number; height: number; unit: string }
  ): Promise<string> => {
    try {
      // Import canvas renderer
      const { CanvasLabelRenderer, templateToCanvasElements } = await import('@/utils/canvasLabelRenderer');
      
      // Calculate canvas dimensions
      let widthPx = 288; // 4 inches at 72 DPI
      let heightPx = 144; // 2 inches at 72 DPI
      
      if (templateDimensions) {
        if (templateDimensions.unit === 'mm') {
          widthPx = (templateDimensions.width / 25.4) * 72;
          heightPx = (templateDimensions.height / 25.4) * 72;
        } else if (templateDimensions.unit === 'in' || templateDimensions.unit === 'inch') {
          widthPx = templateDimensions.width * 72;
          heightPx = templateDimensions.height * 72;
        } else {
          // Assume pixels
          widthPx = templateDimensions.width;
          heightPx = templateDimensions.height;
        }
      }
      
      console.log('🎨 Creating canvas renderer with dimensions:', { widthPx, heightPx });
      
      // Create canvas renderer
      const renderer = new CanvasLabelRenderer({
        width: widthPx,
        height: heightPx,
        dpi: 300, // High DPI for print quality
        backgroundColor: '#ffffff'
      });
      
      // Convert template to canvas elements
      const elements = templateToCanvasElements(template, product);
      console.log('📝 Rendering', elements.length, 'elements');
      
      // Add elements to canvas
      for (const element of elements) {
        await renderer.addElement(element);
      }
      
      if (format === 'pdf') {
        console.log('📄 Converting canvas to PDF');
        return await renderer.toPDF();
      } else {
        // Return as base64 image for HTML content
        console.log('🖼️ Converting canvas to image');
        const imageData = renderer.toDataURL('image/png', 0.95);
        const base64 = imageData.split(',')[1];
        
        // Create simple HTML wrapper around the image
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8">
              <style>
                body { margin: 0; padding: 0; }
                img { width: 100%; height: 100%; object-fit: contain; }
              </style>
            </head>
            <body>
              <img src="${imageData}" alt="Label" />
            </body>
          </html>
        `;
        
        return btoa(unescape(encodeURIComponent(htmlContent)));
      }
    } catch (error) {
      console.error('❌ Canvas rendering failed, falling back to HTML:', error);
      // Fallback to HTML generation
      return await generatePrintContent('', format, templateDimensions);
    }
  };

  const generatePrintContent = async (
    htmlContent: string, 
    format: PrintContentFormat = 'html',
    templateDimensions?: { width: number; height: number; unit: string }
  ): Promise<string> => {
    const fullHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { margin: 0; padding: 0; }
            @media print {
              body { margin: 0; }
              * { -webkit-print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;
    
    if (format === 'pdf') {
      return await generatePdfContent(fullHtml, templateDimensions);
    }
    
    return btoa(unescape(encodeURIComponent(fullHtml)));
  };

  const generatePdfContent = async (
    htmlContent: string, 
    templateDimensions?: { width: number; height: number; unit: string }
  ): Promise<string> => {
    try {
      console.log('🔄 Starting enhanced PDF generation with timeout protection');
      
      // Set a timeout for PDF generation
      const PDF_TIMEOUT = 10000; // 10 seconds
      
      return await Promise.race([
        // Main PDF generation promise
        new Promise<string>((resolve, reject) => {
          try {
            // Dynamic import to avoid issues with SSR
            const jsPDF = require('jspdf').jsPDF;
            
            // Calculate PDF dimensions based on template dimensions or fallback to 4x2 inches
            let widthInches = 4;
            let heightInches = 2;
            
            if (templateDimensions) {
              // Convert template dimensions to inches
              if (templateDimensions.unit === 'mm') {
                widthInches = templateDimensions.width / 25.4;
                heightInches = templateDimensions.height / 25.4;
              } else if (templateDimensions.unit === 'in' || templateDimensions.unit === 'inch') {
                widthInches = templateDimensions.width;
                heightInches = templateDimensions.height;
              } else {
                // Assume pixels and convert using standard 96 DPI
                widthInches = templateDimensions.width / 96;
                heightInches = templateDimensions.height / 96;
              }
            }
            
            // Convert to points (72 points per inch)
            const widthPoints = Math.round(widthInches * 72);
            const heightPoints = Math.round(heightInches * 72);
            
            console.log('📐 PDF dimensions:', { 
              template: templateDimensions, 
              inches: { width: widthInches, height: heightInches },
              points: { width: widthPoints, height: heightPoints }
            });
            
            // Determine orientation based on aspect ratio
            const orientation = widthPoints > heightPoints ? 'landscape' : 'portrait';
            
            const pdf = new jsPDF({
              unit: 'pt',
              format: [widthPoints, heightPoints],
              orientation
            });

            // Create a temporary div to measure content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;
            tempDiv.style.position = 'absolute';
            tempDiv.style.left = '-9999px';
            tempDiv.style.width = `${widthPoints}pt`;
            tempDiv.style.height = `${heightPoints}pt`;
            tempDiv.style.visibility = 'hidden'; // Prevent flickering
            document.body.appendChild(tempDiv);

            // Enhanced PDF generation with better error handling
            pdf.html(tempDiv, {
              callback: function (doc) {
                try {
                  // Clean up
                  if (document.body.contains(tempDiv)) {
                    document.body.removeChild(tempDiv);
                  }
                  
                  // Get PDF as base64
                  const pdfBase64 = doc.output('datauristring').split(',')[1];
                  console.log('✅ PDF generated successfully, size:', pdfBase64.length);
                  resolve(pdfBase64);
                } catch (error) {
                  console.error('❌ PDF callback error:', error);
                  if (document.body.contains(tempDiv)) {
                    document.body.removeChild(tempDiv);
                  }
                  reject(new Error(`PDF callback failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
                }
              },
              margin: [1, 1, 1, 1], // Minimal margins
              autoPaging: false,
              html2canvas: {
                allowTaint: true,
                dpi: 72, // Further reduced for reliability
                letterRendering: false,
                useCORS: false,
                scale: 0.75, // Further reduced scale to prevent memory issues
                logging: false,
                backgroundColor: '#ffffff',
                removeContainer: true,
                foreignObjectRendering: false, // Disable for better compatibility
                imageTimeout: 5000, // 5 second timeout for images
                onclone: (clonedDoc: Document) => {
                  // Simplify styles in cloned document
                  const style = clonedDoc.createElement('style');
                  style.textContent = `
                    * { 
                      font-family: Arial, sans-serif !important; 
                      -webkit-print-color-adjust: exact !important;
                      color-adjust: exact !important;
                    }
                  `;
                  clonedDoc.head.appendChild(style);
                }
              }
            });
          } catch (error) {
            console.error('❌ PDF generation setup failed:', error);
            reject(new Error(`PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
          }
        }),
        
        // Timeout promise
        new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error(`PDF generation timed out after ${PDF_TIMEOUT}ms`));
          }, PDF_TIMEOUT);
        })
      ]);
    } catch (error) {
      console.error('❌ Enhanced PDF generation failed:', error);
      throw new Error(`Enhanced PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return {
    printers,
    selectedPrinter,
    loading,
    error,
    setSelectedPrinter,
    fetchPrinters,
    submitPrintJob,
    generatePrintContent,
    generateCanvasPrintContent,
    generatePdfContent,
  };
};